import {useMemo, useState} from "react";
import {PersonEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import { getFormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UsePersonDataTableFilterProps = Omit<UseDataTableFilterProps<PersonEntity>, 'filterConfig'>

export function usePersonDataTableFilter(props: UsePersonDataTableFilterProps): UseDataTableFilterResult<PersonEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [firstNameQuery, setFirstNameQuery] = useState<string | undefined>(undefined);
  const [firstNameDebouncedQuery] = useDebouncedValue(firstNameQuery, 200);

  const [lastNameQuery, setLastNameQuery] = useState<string | undefined>(undefined);
  const [lastNameDebouncedQuery] = useDebouncedValue(lastNameQuery, 200);

  const [typeQuery, setTypeQuery] = useState<string | undefined>(undefined);
  const [typeDebouncedQuery] = useDebouncedValue(typeQuery, 200);

  const [barcodeQuery, setBarcodeQuery] = useState<string | undefined>(undefined);
  const [barcodeDebouncedQuery] = useDebouncedValue(barcodeQuery, 200);

  const [emailAddressQuery, setEmailAddressQuery] = useState<string | undefined>(undefined);
  const [emailAddressDebouncedQuery] = useDebouncedValue(emailAddressQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const filterConfig: DataTableFilterConfig<PersonEntity> = useMemo(() => {
    return {
      "firstName": {
        setQuery: setFirstNameQuery,
        query: firstNameQuery,
        debouncedQuery: firstNameDebouncedQuery,
        filter: (record, query) => !record.firstName.toLowerCase().includes(query.toLowerCase())
      },
      "lastName": {
        setQuery: setLastNameQuery,
        query: lastNameQuery,
        debouncedQuery: lastNameDebouncedQuery,
        filter: (record, query) => !record.lastName.toLowerCase().includes(query.toLowerCase())
      },
      "type": {
        setQuery: setTypeQuery,
        query: typeQuery,
        debouncedQuery: typeDebouncedQuery,
        filter: ({type}, query) => {
          if (type) {
            const display = getFormattedDatabaseCode(type, t)
            return !type.code.toLowerCase().includes(query.toLowerCase()) || !display || !display.toLowerCase().includes(query.toLowerCase())
          } else {
            return false
          }
        }
      },
      "barcode": {
        setQuery: setBarcodeQuery,
        query: barcodeQuery,
        debouncedQuery: barcodeDebouncedQuery,
        filter: ({barcode}, query) => !barcode || !barcode.toLowerCase().includes(query.toLowerCase())
      },
      "emailAddress": {
        setQuery: setEmailAddressQuery,
        query: emailAddressQuery,
        debouncedQuery: emailAddressDebouncedQuery,
        filter: ({emailAddress}, query) => !emailAddress || !emailAddress.toLowerCase().includes(query.toLowerCase())
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    firstNameQuery,
    firstNameDebouncedQuery,
    lastNameQuery,
    lastNameDebouncedQuery,
    typeQuery,
    typeDebouncedQuery,
    barcodeQuery,
    barcodeDebouncedQuery,
    emailAddressQuery,
    emailAddressDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
